import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageMeta from '@/components/base/PageMeta';
import { comics, categories, featuredBanners } from '@/mocks/comics';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % featuredBanners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const hotComics = comics.filter((c) => c.isHot);
  const newComics = comics.filter((c) => c.isNew);
  const recommendedComics = comics.filter((c) => c.isRecommended);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('订阅成功！感谢您的关注。');
      form.reset();
      return;
    }
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pc4paftmtnfcfh0kcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('订阅成功！感谢您的关注，我们将为您推送最新漫画资讯。');
        form.reset();
      } else {
        setFormStatus('error');
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || '订阅失败，请稍后重试。';
        setFormMessage(msg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <PageMeta
        title="韩国漫画-韩漫大全_热门漫画推荐_免费在线观看-韩漫网"
        description="韩漫网打造齐全的韩漫大全，还有专业的热门漫画推荐，所有韩国漫画内容均可免费在线观看。涵盖恋爱、奇幻、动作、悬疑等多种题材，每日更新最新韩漫章节。"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '韩漫网 - 韩国漫画大全',
          description: '韩漫网打造齐全的韩漫大全，免费在线观看热门韩国漫画。',
          url: 'https://hanmanwang.com/',
          mainEntity: {
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '韩漫网上的漫画都是免费的吗？', acceptedAnswer: { '@type': 'Answer', text: '是的，韩漫网所有韩国漫画内容均免费在线观看，无需付费订阅。' } },
              { '@type': 'Question', name: '漫画更新频率是怎样的？', acceptedAnswer: { '@type': 'Answer', text: '热门连载漫画保持每日更新，让读者不错过任何精彩章节。' } },
              { '@type': 'Question', name: '如何找到我想看的漫画类型？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过顶部的分类导航浏览不同类型的漫画，也可以使用搜索功能输入关键词快速找到心仪的作品。' } },
              { '@type': 'Question', name: '支持手机阅读吗？', acceptedAnswer: { '@type': 'Answer', text: '韩漫网采用响应式设计，完美适配手机、平板和电脑等各类设备。' } },
              { '@type': 'Question', name: '有APP可以下载吗？', acceptedAnswer: { '@type': 'Answer', text: '目前韩漫网为网页版平台，我们正在积极开发移动端应用，敬请期待。' } },
            ],
          },
        }}
      />
      <Navbar />
      <ScrollToTop />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {featuredBanners.map((banner, index) => (
            <div
              key={banner.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentBanner ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={banner.bgImage}
                alt={banner.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>

          <div className="absolute bottom-0 left-0 right-0 z-10">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10 md:pb-16">
              {featuredBanners.map((banner, index) => (
                <div
                  key={banner.id}
                  className={`transition-all duration-500 ${
                    index === currentBanner
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-4 absolute invisible'
                  }`}
                >
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-500 text-foreground-950 rounded-full mb-3 whitespace-nowrap">
                    今日推荐
                  </span>
                  <h2 className="text-3xl md:text-5xl font-bold font-heading text-white mb-2">
                    {banner.title}
                  </h2>
                  <p className="text-base md:text-lg text-white/85 mb-1">{banner.subtitle}</p>
                  <p className="text-sm text-white/65 mb-5 max-w-xl">{banner.description}</p>
                  <Link
                    to={`/comic/${banner.comicId}`}
                    className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    立即阅读 <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
            {featuredBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentBanner
                    ? 'bg-white w-6'
                    : 'bg-white/40 hover:bg-white/70'
                }`}
                aria-label={`切换到第${index + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Hot Comics */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                  <i className="ri-fire-line text-primary-500 mr-2"></i>
                  热门漫画
                </h2>
                <p className="text-sm text-foreground-500 mt-1">全网热度最高的韩国漫画推荐</p>
              </div>
              <Link
                to="/category/love"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {hotComics.slice(0, 6).map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                漫画分类
              </h2>
              <p className="text-sm text-foreground-500 mt-1">探索你喜欢的漫画类型</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-50 hover:bg-primary-50 transition-all duration-300"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-white transition-all duration-300">
                    <i className={`${cat.icon} text-2xl`}></i>
                  </div>
                  <div className="text-center">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 whitespace-nowrap">{cat.name}</h3>
                    <p className="text-xs text-foreground-500 mt-1">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                  <i className="ri-time-line text-accent-500 mr-2"></i>
                  最新上架
                </h2>
                <p className="text-sm text-foreground-500 mt-1">刚刚更新的新鲜漫画</p>
              </div>
              <Link
                to="/category/fantasy"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {newComics.slice(0, 6).map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Recommended */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                  <i className="ri-thumb-up-line text-secondary-500 mr-2"></i>
                  编辑推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">精选优质韩漫，不容错过</p>
              </div>
              <Link
                to="/category/drama"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {recommendedComics.map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: 'ri-book-read-line', value: '10,000+', label: '漫画作品', color: 'text-primary-500', bg: 'bg-primary-50' },
                { icon: 'ri-user-heart-line', value: '500万+', label: '活跃读者', color: 'text-accent-500', bg: 'bg-accent-100' },
                { icon: 'ri-timer-line', value: '每日更新', label: '更新频率', color: 'text-secondary-500', bg: 'bg-secondary-100' },
                { icon: 'ri-star-line', value: '4.8', label: '平均评分', color: 'text-primary-500', bg: 'bg-primary-50' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-100">
                  <div className={`w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl ${stat.bg}`}>
                    <i className={`${stat.icon} text-xl ${stat.color}`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-1">关于韩漫网的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {[
                { q: '韩漫网上的漫画都是免费的吗？', a: '是的，韩漫网所有韩国漫画内容均免费在线观看，无需付费订阅。我们致力于为漫画爱好者提供优质的免费阅读体验。' },
                { q: '漫画更新频率是怎样的？', a: '热门连载漫画保持每日更新，让读者不错过任何精彩章节。具体更新频率以各漫画作品页面显示为准。' },
                { q: '如何找到我想看的漫画类型？', a: '您可以通过顶部的分类导航浏览不同类型的漫画，也可以使用搜索功能输入关键词快速找到心仪的作品。' },
                { q: '支持手机阅读吗？', a: '韩漫网采用响应式设计，完美适配手机、平板和电脑等各类设备，让您随时随地享受阅读乐趣。' },
                { q: '有APP可以下载吗？', a: '目前韩漫网为网页版平台，您可以直接通过浏览器访问使用。我们正在积极开发移动端应用，敬请期待。' },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform text-lg shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className="py-10 md:py-14 bg-primary-500">
          <div className="max-w-[700px] mx-auto px-4 md:px-6 text-center">
            <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-white/20">
              <i className="ri-mail-send-line text-2xl text-white"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-white mb-2">
              订阅我们的更新通知
            </h2>
            <p className="text-sm text-white/80 mb-6">
              第一时间获取最新漫画更新、热门推荐和独家资讯
            </p>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form=""
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                type="text"
                name="phone_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <input
                type="email"
                name="email"
                placeholder="请输入您的邮箱地址"
                required
                className="flex-1 px-4 py-3 text-sm bg-white border-0 rounded-full text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-foreground-950 hover:bg-foreground-900 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap"
              >
                立即订阅
              </button>
            </form>
            {formStatus !== 'idle' && (
              <p className={`mt-3 text-sm ${formStatus === 'success' ? 'text-white' : 'text-white/90'}`}>
                {formMessage}
              </p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}